// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { get } from '@tls/auth';
import { HistoryInformationMessage } from '@tls/sw91-communication/types/com.base';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HubContext } from '../components/common/emitter/HubContext';
import sortBy from 'lodash/sortBy';
import { setPredictEnabled } from 'store/reducers/projectLabelSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useQuery } from '@tanstack/react-query';
import { GenericResponse } from 'model/reponse/GenericResponse';
import { GetHistoryInformationAckMessages } from 'model/reponse/GetHistoryInformationAckMessages';

export const getProjectTrainingHistoryQueryKey = (projectId: string) => [`projects/${projectId}/training/history`];

type UseProjectTrainingApiOptions = {
  enabled?: boolean;
  showErrorToast?: boolean;
};

export default function useProjectTrainingApi(projectId: string, options?: UseProjectTrainingApiOptions) {
  const { enabled = true, showErrorToast = true } = options ?? {};
  const queryEnabled = enabled && Boolean(projectId);
  const [history, setHistory] = useState<HistoryInformationMessage[]>([]);

  const dispatch = useAppDispatch();
  const predictEnabled = useAppSelector(s => s.label.predictEnabled);

  const listener = useCallback(
    (message: HistoryInformationMessage) => {
      if (history.some(h => h.epochNumber === message.epochNumber)) {
        console.warn('Duplicate epoch message', message);
        return;
      }

      if (message.modelUpdate && !predictEnabled) {
        dispatch(setPredictEnabled(true));
      }

      const newHistory = [...history, message];
      setHistory(sortBy(newHistory, ['epochNumber']));
    },
    [dispatch, history, predictEnabled]
  );

  const { events } = useContext(HubContext);
  useEffect(() => {
    if (!queryEnabled) return undefined;

    events.on('onHistoryInformationUpdate', listener);

    return () => {
      events.off('onHistoryInformationUpdate', listener);
    };
  }, [events, listener, queryEnabled]);

  const { t } = useTranslation();

  const { data, isLoading, status, isError } = useQuery<GenericResponse<GetHistoryInformationAckMessages>>({
    queryKey: getProjectTrainingHistoryQueryKey(projectId),
    queryFn: async () => {
      const response = await get(`projects/${projectId}/training/history`);
      if (typeof response?.status !== 'number' || response.status < 200 || response.status >= 300) {
        throw new Error(`Unable to load training history for project ${projectId}`);
      }
      return response;
    },
    enabled: queryEnabled,
    retry: false,
  });

  const historyInformation = data?.data?.historyInformation;
  const convertedHistory = useMemo(
    () => historyInformation?.map(HistoryInformationMessage.fromJSON) ?? [],
    [historyInformation]
  );

  useEffect(() => {
    if (!showErrorToast || !isError) return;
    ToastByStatus(500, t('project.label.training.title'), t('project.label.training.changeFailed'));
  }, [isError, showErrorToast, t]);

  useEffect(() => {
    if (!queryEnabled) {
      setHistory([]);
      return;
    }

    if (!historyInformation) return;

    dispatch(setPredictEnabled(convertedHistory.some(h => h.modelUpdate)));
    setHistory(convertedHistory);
  }, [convertedHistory, dispatch, historyInformation, queryEnabled]);

  const hasModelInResponse = convertedHistory.some(h => h.modelUpdate);
  const hasModel = hasModelInResponse || history.some(h => h.modelUpdate);

  return { history, hasModel, status, loading: isLoading };
}
